import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Loader } from "storybook/internal/components";
import canclecircle from "../../../../assets/images/cancelcircle.svg";
import loader from "../../../../assets/images/Spinner2.svg";
import Button from "../../../../components/common/Button/Button";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import PolicyCard from "../../../../components/policyCard/policyCard";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import usePolicyCancellationFlow from "../../../../hooks/usePolicyCancellationFlow";
import { selectCrmCaseComplaintLoader } from "../../../../redux/features/crm/caseComplaintSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import AlertComponent from "../../../motorInsurancePages/transferPolicyFlow/alertComponent";
import PolicyCancellationMotorLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/policyCancellationMotorLayout";

const SmartDeviceConfirmCancellationPage = ({
  handleNavigationToPolicyOverview,
  confirmedCancellation,
  selectedReasonOut,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const translate = useLanguageTranslate();
  const { handlePrevStep } = usePolicyCancellationFlow();

  // Get policy detail from Redux (same selector as travel uses)
  const policyDetailsInfo = useSelector((state) => state.policyDetails?.PolicyDetails?.PolicyHeader);

  // Create smart device policy card info (adapting motor structure for smart device)
  const policyCardInfo = {
    ModelYear: "",
    Make: "Smart Device Insurance", // Smart Device Policy
    Model: policyDetailsInfo?.DeviceTypeName || policyDetailsInfo?.DeviceType || policyDetailsInfo?.DeviceName || "Device Protection", // Device type
    CPR: "",
    PolicyNumber: policyDetailsInfo?.PolicyNumber || "--",
    RegistrationNumber: "", // Not applicable for smart device
    SumInsured: 0.0,
    PlanName: policyDetailsInfo?.PlanName || "--",
    ProductCode: "0213", // Smart Device insurance product code
    ProductName: "Smart Device",
    StartDate:
      policyDetailsInfo?.FromDateStr?.split(" ")[0]
        ?.split("/")
        ?.reverse()
        ?.join("-") || "--",
    EndDate:
      policyDetailsInfo?.ToDateStr?.split(" ")[0]
        ?.split("/")
        ?.reverse()
        ?.join("-") || "--",
    FromDateStr: policyDetailsInfo?.FromDateStr?.split(" ")[0] || "--",
    ToDateStr: policyDetailsInfo?.ToDateStr?.split(" ")[0] || "--",
    Branch: getClientSource(),
    MainChannel: "Direct Bahrain",
    Renewed: policyDetailsInfo?.IsRenewed,
    PolicyStatus: "Active",
    IsRenewable: false,
    // Add these fields for NonMotorPolicyCard to display correctly
    DeviceTypeName: policyDetailsInfo?.DeviceTypeName,
    DeviceType: policyDetailsInfo?.DeviceType,
    DeviceName: policyDetailsInfo?.DeviceName,
  };

  console.log("Selected reason from final page: ", selectedReasonOut);
  const isCaseLoading = useSelector(selectCrmCaseComplaintLoader);
  
  return (
    <PolicyCancellationMotorLayout
      backClickHandler={handlePrevStep}
      is_bottom_sticky_layout={false}
    >
      <div className="flex flex-col items-center mx-auto max-w-lg max767:p-5 max767:mb-8 max767:pt-0 pt-[1%]">
        <div className="flex justify-center items-center w-full">
          <div className=" p-0 w-full confirmCancellation">
            <TitleDescription
              title={translate(
                "Confirm Cancellation",
                "تأكيد الإلغاء"
              )}
              description={translate(
                "Review your policy details below to ensure you are fully informed before finalizing the cancellation of your policy.",
                "يُرجى مراجعة تفاصيل سياسة التأمين أدناه للتأكد من أنك على دراية تامة قبل إنهاء الإلغاء."
              )}
            />
            <div className="flex flex-col items-center gap-2 self-stretch bg-[#F6F8F9] p-4 rounded-2xl mb-4 max767:p-0 max767:bg-white">
              <p className="text-[#48535B] text-base not-italic font-semibold leading-6 tracking-[-0.16px] gap-2 flex">
                <img src={canclecircle} alt="" />{" "}
                {translate(
                  "You are cancelling this policy",
                  "أنت تلغي هذه السياسة"
                )}
              </p>
              <PolicyCard
                className="mb-4"
                policy_name="Smart Device Insurance"
                policy_label={policyDetailsInfo?.DeviceTypeName || policyDetailsInfo?.DeviceType || policyDetailsInfo?.DeviceName || "Smart Device Policy"}
                policy_number={policyDetailsInfo?.PolicyNumber || "N/A"}
                from_date={policyCardInfo.FromDateStr}
                to_date={policyCardInfo.ToDateStr}
                policy_bg_image={policyCardInfo.policy_bg_image}
                expire_days={policyCardInfo.expire_days}
                is_text_dark={policyCardInfo.is_text_dark}
                mapping_key={policyCardInfo.id}
                data={policyCardInfo}
                disableCardClick
              />
            </div>

            <AlertComponent
              title={translate(
                "Please note that once you confirm cancellation, this request will be processed and cannot be reversed. Ensure all details are correct and that you understand the implications of proceeding.",
                "يرجى ملاحظة أنه بمجرد تأكيد الإلغاء، ستتم معالجة هذا الطلب ولا يمكن التراجع عنه. تأكد من أن جميع التفاصيل صحيحة وأنك تفهم الآثار المترتبة على المتابعة."
              )}
            />
            <div className="max767:footer max767:bg-white max767:border-t-[#E5E9EB] max767:border-t max767:border-solid max767:mt-2 max767:w-full max767:py-2 flex max767:flex-col !gap-4 mt-4 max767:fixed max767:bottom-0 max767:w-full max767:p-4 max767:inset-x-0 ipad:items-center max767:inline-block max767:z-[9] rtl:grid rtl:grid-cols-2 max767:!flex">
              <Button
                label={translate("Keep Policy", "الإبقاء على السياسة")}
                onSubmit={handleNavigationToPolicyOverview}
                disabled={isCaseLoading}
                className={`flex flex-col items-center max-w-[100%] gap-2.5 flex-[1_0_0] border max767:text-sm bg-white text-[#1A2024] text-center text-lg not-italic font-semibold leading-6 tracking-[-0.18px] p-4 rounded-[10px] border-solid border-[#DDE2E4] hover:bg-[#F6F8F9] rtl:w-max max767:w-full max767:mb-4 max767:!h-[56px] max767:min-h-[56px] max767:!max-h-[56px] rtl:!w-full max767:!mb-1 ${isCaseLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              />
              <Button
                label={
                  isCaseLoading ? (
                    <span className="h-[24px] w-[24px] loader flex items-center justify-center">
                      <img className="h-full w-full" src={loader} alt="loader" />
                    </span>
                  ) : (
                    translate("Confirm Cancellation", "تأكيد الإلغاء")
                  )
                }
                onSubmit={confirmedCancellation}
                disabled={isCaseLoading}
                className={`flex w-[300px] max-w-[100%] max767:w-full max767:text-sm flex-col items-center gap-2.5 bg-[#CC0905] text-white text-center text-lg not-italic font-semibold leading-6 tracking-[-0.18px] p-4 rounded-[10px] canclebutton hover:bg-[#CC0905] rtl:w-max max767:w-full max767:!h-[56px] max767:min-h-[56px] max767:!max-h-[56px] rtl:!w-full ${isCaseLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                labelStyle="!text-white"
              />
            </div>
          </div>
        </div>
      </div>
    </PolicyCancellationMotorLayout>
  );
};

export default SmartDeviceConfirmCancellationPage;